"use strict";
/**
 * Notification & Error UI
 * Handles error messages, loading states, and status notifications
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.showError = showError;
exports.hideError = hideError;
exports.showEventsLoading = showEventsLoading;
exports.showOrderStatus = showOrderStatus;
exports.hideOrderStatus = hideOrderStatus;
exports.showLoadingSpinner = showLoadingSpinner;
exports.hideLoadingSpinner = hideLoadingSpinner;
exports.showOrderbookError = showOrderbookError;
exports.hideOrderbookError = hideOrderbookError;
exports.showPositionError = showPositionError;
exports.hidePositionError = hidePositionError;
exports.showOrderError = showOrderError;
exports.hideOrderError = hideOrderError;
exports.showFillCountBadge = showFillCountBadge;
exports.showBorderGlow = showBorderGlow;
const DEFAULT_ERROR_HIDE_MS = 5000;
const errorHideTimeouts = new Map();
/**
 * Show error message
 */
function showError(message, elementId = "login-error", autoHideMs) {
    const element = document.getElementById(elementId);
    if (element) {
        const existingTimeoutId = errorHideTimeouts.get(elementId);
        if (existingTimeoutId !== undefined) {
            clearTimeout(existingTimeoutId);
            errorHideTimeouts.delete(elementId);
        }
        // Show the element
        element.classList.remove("hidden");
        // Find a specific place for the text
        const textContainer = element.querySelector(".error-text-target");
        if (textContainer) {
            textContainer.textContent = message;
        }
        else {
            // Fallback if no specific container exists
            element.textContent = message;
        }
        const resolvedAutoHideMs = autoHideMs
            ?? (element.classList.contains("error-message")
                ? DEFAULT_ERROR_HIDE_MS
                : 0);
        if (resolvedAutoHideMs > 0) {
            const timeoutId = window.setTimeout(() => {
                hideError(elementId);
            }, resolvedAutoHideMs);
            errorHideTimeouts.set(elementId, timeoutId);
        }
    }
}
/**
 * Hide error message
 */
function hideError(elementId = "login-error") {
    const existingTimeoutId = errorHideTimeouts.get(elementId);
    if (existingTimeoutId !== undefined) {
        clearTimeout(existingTimeoutId);
        errorHideTimeouts.delete(elementId);
    }
    const element = document.getElementById(elementId);
    if (element) {
        element.classList.add("hidden");
    }
}
/**
 * Show loading state for markets
 */
function showEventsLoading(isLoading) {
    const loading = document.getElementById("events-loading");
    const list = document.getElementById("events-list");
    if (loading) {
        if (isLoading) {
            loading.classList.remove("hidden");
            if (list)
                list.style.opacity = "0.5";
        }
        else {
            loading.classList.add("hidden");
            if (list)
                list.style.opacity = "1";
        }
    }
}
/**
 * Show order placement status
 */
function showOrderStatus(message, type) {
    const statusElement = document.getElementById("order-status");
    const statusText = document.getElementById("order-status-text");
    if (statusElement && statusText) {
        statusText.textContent = message;
        statusElement.className = `order-status ${type}`;
        statusElement.classList.remove("hidden");
    }
}
/**
 * Hide order status
 */
function hideOrderStatus() {
    const statusElement = document.getElementById("order-status");
    if (statusElement) {
        statusElement.classList.add("hidden");
    }
}
/**
 * Show loading spinner
 */
function showLoadingSpinner(message = "Loading...") {
    const spinner = document.getElementById("loading-spinner");
    const spinnerText = document.getElementById("loading-spinner-text");
    if (spinnerText)
        spinnerText.textContent = message;
    if (spinner)
        spinner.classList.remove("hidden");
}
/**
 * Hide loading spinner
 */
function hideLoadingSpinner() {
    const spinner = document.getElementById("loading-spinner");
    if (spinner) {
        spinner.classList.add("hidden");
    }
}
/**
 * Show a centered, semi-transparent floating error overlay.
 *
 * Shared by the orderbook, position, and order-failure surfaces: each floats
 * above the page (appended to <body>), dismisses on click, and auto-hides after
 * `autoHideMs`. The element is created once per id and reused on later calls.
 */
function showFloatingErrorOverlay(id, className, message, autoHideMs) {
    let errorElement = document.getElementById(id);
    if (!errorElement) {
        errorElement = document.createElement("div");
        errorElement.id = id;
        errorElement.className = className;
        // Append to body instead of inline to float above page
        document.body.appendChild(errorElement);
        // Add click-to-dismiss functionality
        errorElement.addEventListener("click", () => {
            hideFloatingErrorOverlay(id);
        });
    }
    errorElement.textContent = `⚠️ ${message}`;
    errorElement.classList.remove("hidden");
    setTimeout(() => {
        hideFloatingErrorOverlay(id);
    }, autoHideMs);
}
/**
 * Hide a floating error overlay by id.
 */
function hideFloatingErrorOverlay(id) {
    const errorElement = document.getElementById(id);
    if (errorElement) {
        errorElement.classList.add("hidden");
    }
}
/**
 * Show orderbook polling error
 * Displays as a floating overlay that dismisses on click
 */
function showOrderbookError(message) {
    showFloatingErrorOverlay("orderbook-error", "orderbook-error", message, 3000);
}
/**
 * Hide orderbook error
 */
function hideOrderbookError() {
    hideFloatingErrorOverlay("orderbook-error");
}
/**
 * Show position polling error
 * Displays as a floating overlay that dismisses on click
 */
function showPositionError(message) {
    showFloatingErrorOverlay("position-error", "position-error", message, 5000);
}
/**
 * Hide position error
 */
function hidePositionError() {
    hideFloatingErrorOverlay("position-error");
}
/**
 * Show a failed-order error as a centered floating overlay.
 *
 * Covers both pre-submit validation ("No market selected") and exchange
 * rejections. Uses the same click-to-dismiss + auto-hide convention as the
 * orderbook/position overlays so every failure surfaces the same way, centered
 * on screen instead of scrolling off the bottom of the order panel.
 */
function showOrderError(message) {
    showFloatingErrorOverlay("order-error-overlay", "order-error-overlay", message, 5000);
}
/**
 * Hide order error overlay
 */
function hideOrderError() {
    hideFloatingErrorOverlay("order-error-overlay");
}
/**
 * Show a small floating fill-count badge in the top-right corner.
 *
 * The badge floats upward and fades out over ~3 seconds, then is removed from
 * the DOM.  Multiple calls stack independently — each badge animates on its own.
 *
 * @param filledQty - Number of contracts/shares filled (may be fractional)
 * @param side - "yes" for a green badge, "no" for a red badge
 */
function showFillCountBadge(filledQty, side) {
    const BADGE_LIFETIME_MS = 3000;
    const badge = document.createElement("div");
    badge.className = `fill-count-badge fill-count-badge--${side}`;
    const displayQty = Number.isInteger(filledQty) ? String(filledQty) : filledQty.toFixed(2);
    badge.textContent = `+${displayQty} filled`;
    document.body.appendChild(badge);
    setTimeout(() => {
        badge.remove();
    }, BADGE_LIFETIME_MS);
}
/**
 * Show border glow effect on the screen
 * @param type - "success" for green glow, "error" for red glow
 */
function showBorderGlow(type) {
    const body = document.body;
    const glowClass = type === "success" ? "border-glow-success" : "border-glow-error";
    body.classList.add(glowClass);
    // Remove the glow after 800ms
    setTimeout(() => {
        body.classList.remove(glowClass);
    }, 800);
}
